import axios from 'axios'
import { showLoading, hideLoading } from '@/utils/loadingManager'
import { createPinia } from 'pinia'
import { useAppStore } from '@/stores/appStore'

const BASE_URL = import.meta.env.VITE_API_BASE_URL
const VITE_SERVICE_BASE_URL = import.meta.env.VITE_SERVICE_BASE_URL

let piniaInstance = null

export const setPinia = (pinia) => {
  piniaInstance = pinia
}

// 安全获取 appStore 的方法
const getAppStore = () => {
  if (piniaInstance) {
    return useAppStore(piniaInstance)
  }
  // 尝试直接获取（适用于已经初始化 pinia 的情况）
  try {
    return useAppStore()
  } catch (e) {
    console.warn('Pinia not initialized yet, cannot get appStore')
    return null
  }
}

const request = axios.create({
  baseURL: BASE_URL,
  timeout: 180000
})

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    // console.log('发送请求:', config)

    if (config.showLoading !== false) {
      showLoading()
    }

    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `token ${token}`
    }

    return config
  },
  (error) => {
    // 对请求错误做些什么
    console.error('请求错误:', error)
    hideLoading()

    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  (response) => {
    if (response.config.showLoading !== false) {
      hideLoading()
    }

    const appStore = getAppStore()

    const responseData = response.data

    if (responseData) {
      if (responseData.info === '请登录！' || responseData.code === 401) {
        localStorage.removeItem('token')
        localStorage.removeItem('user_id')

        if (appStore) {
          appStore.setLoginVisible(true)
        }
      }

      // 处理 code 40301：需要绑定手机号
      if (responseData.code === 40301) {
        if (appStore) {
          appStore.showBindPhone()
        }
      }
    }

    return responseData
  },
  (error) => {
    hideLoading()

    console.error('响应错误:', error)

    if (error.response) {
      const appStore = getAppStore()

      // 根据响应状态码做不同的处理
      switch (error.response.status) {
        case 401:
          // 未授权/未登录：清除本地凭证并显示登录弹窗
          localStorage.removeItem('token')
          localStorage.removeItem('user_id')
          if (appStore) {
            appStore.setLoginVisible(true)
          }
          break
        case 403:
          // 禁止访问
          // console.warn('禁止访问')
          break
        case 404:
          // 请求资源不存在
          // console.warn('请求资源不存在')
          break
        case 500:
          // 服务器内部错误
          // console.warn('服务器内部错误')
          break
        default:
        // console.warn(`其他错误 (${error.response.status})`)
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应
      console.error('网络错误，请检查网络连接')
    } else {
      // 其他错误
      console.error('未知错误:', error.message)
    }

    return Promise.reject(error)
  }
)

const buildFormData = (fields) => {
  const formData = new FormData()
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value)
  })
  return formData
}

/**
 * GET请求
 * @param {string} url - 请求地址
 * @param {object} params - 请求参数
 * @param {boolean} showLoading - 是否显示加载中
 * @returns {Promise}
 */
export const requestGet = ({
  url,
  params = {},
  baseURL = BASE_URL,
  showLoading = true
}) => {
  return request({
    method: 'GET',
    url,
    params,
    baseURL,
    showLoading
  })
}

/**
 * POST请求
 * @param {string} url - 请求地址
 * @param {object} data - 请求数据
 * @returns {Promise}
 */
export const requestPost = ({
  url,
  data = {},
  isFormData = true,
  baseURL = BASE_URL,
  showLoading = true
}) => {
  return request({
    method: 'POST',
    url,
    data: isFormData ? buildFormData(data) : data,
    baseURL,
    showLoading
  })
}

/**
 * PUT请求
 * @param {string} url - 请求地址
 * @param {object} data - 请求数据
 * @returns {Promise}
 */
export const requestPut = ({
  url,
  data = {},
  isFormData = true,
  baseURL = BASE_URL,
  showLoading = true
}) => {
  return request({
    method: 'PUT',
    url,
    data: isFormData ? buildFormData(data) : data,
    baseURL,
    showLoading
  })
}

/**
 * DELETE请求
 * @param {string} url - 请求地址
 * @param {object} data - 请求数据
 * @returns {Promise}
 */
export const requestDel = ({
  url,
  data = null,
  isFormData = true,
  baseURL = BASE_URL,
  showLoading = true
}) => {
  const config = {
    method: 'DELETE',
    url,
    baseURL,
    showLoading
  }

  if (data) {
    config.data = isFormData ? buildFormData(data) : data
  }

  return request(config)
}
